import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsListItem from '@/components/feature/NewsListItem';
import { useFavorites } from '@/hooks/useFavorites';
import { newsList } from '@/mocks/newsData';
import type { NewsItem } from '@/types';
import { formatViews } from '@/utils/format';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 350);
    return () => clearTimeout(timer);
  }, [id]);

  const item = (newsList as NewsItem[]).find((entry) => entry.id === id);

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).catch(() => undefined);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <Layout>
        <div className="mx-auto max-w-portal px-4 py-16 md:px-6">
          <div className="flex flex-col items-center justify-center text-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" aria-hidden="true"></i>
            <p className="mt-4 text-sm text-foreground-500">正在加载资讯内容…</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="mx-auto max-w-portal px-4 py-16 md:px-6">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-article-line" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-base font-medium text-foreground-800">未找到该篇资讯</p>
            <p className="mt-1 text-sm text-foreground-500">内容可能已被移除或链接有误，请返回资讯列表。</p>
            <Link
              to="/category/investment-news"
              className="mt-5 rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400 whitespace-nowrap"
            >
              返回投资资讯
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const favorited = isFavorite(item.id);
  const related = (newsList as NewsItem[])
    .filter((entry) => entry.id !== item.id)
    .sort((a, b) => (a.tag === item.tag ? -1 : 0) - (b.tag === item.tag ? -1 : 0))
    .slice(0, 3);

  return (
    <Layout>
      <title>{`${item.title} - 黄金`}</title>
      <meta name="description" content={item.summary} />
      <meta name="keywords" content={`${item.tag},黄金资讯,黄金投资,${item.title}`} />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-portal px-4 py-6 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '投资资讯', to: '/category/investment-news' },
              { label: item.title },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto max-w-portal px-4 py-8 md:px-6 md:py-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
          <article>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <span className="inline-block rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                {item.tag}
              </span>
              <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                {item.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line" aria-hidden="true"></i>
                  {item.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  {item.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line" aria-hidden="true"></i>
                  {formatViews(item.views)} 阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {item.readTime}
                </span>
              </div>

              <div className="mt-6 overflow-hidden rounded-lg">
                <img
                  src={item.image}
                  alt={item.title}
                  title={`${item.title} 黄金资讯`}
                  className="h-64 w-full object-top md:h-80"
                />
              </div>

              <div className="mt-6 space-y-5">
                {item.content.map((paragraph, index) => (
                  <p key={index} className="text-[15px] leading-8 text-foreground-800">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
                <button
                  type="button"
                  onClick={() => toggleFavorite(item.id)}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    favorited
                      ? 'border-primary-400 bg-primary-50 text-primary-800'
                      : 'border-background-300 text-foreground-700 hover:border-primary-300'
                  }`}
                >
                  <i className={favorited ? 'ri-star-fill text-primary-600' : 'ri-star-line'} aria-hidden="true"></i>
                  {favorited ? '已收藏' : '收藏文章'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap cursor-pointer"
                >
                  <i className={copied ? 'ri-check-line text-accent-600' : 'ri-share-line'} aria-hidden="true"></i>
                  {copied ? '链接已复制' : '分享'}
                </button>
                <span className="text-xs text-foreground-400">收藏状态保存在本设备，仅用于演示</span>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-4 text-xs leading-relaxed text-primary-900 md:p-5 md:text-sm">
              <strong className="font-semibold">免责声明：</strong>
              本文内容为演示样例，仅供参考，不构成任何投资建议。市场有风险，投资需谨慎。
            </div>
          </article>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="flex flex-col gap-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                  <i className="ri-bookmark-line text-primary-600" aria-hidden="true"></i>
                  相关阅读
                </h2>
                <ul className="mt-3 divide-y divide-background-200">
                  {related.map((news) => (
                    <li key={news.id} className="py-3 first:pt-0 last:pb-0">
                      <Link to={`/detail/news/${news.id}`} className="group flex flex-col gap-1.5">
                        <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                          {news.title}
                        </span>
                        <span className="text-xs text-foreground-400">
                          {news.tag} · {news.date}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-newspaper-line text-primary-600" aria-hidden="true"></i>
            更多投资资讯
          </h2>
          <div className="mt-5 grid gap-4">
            {related.map((news) => (
              <NewsListItem key={`more-${news.id}`} item={news} />
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}